import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ControlledInput, ControlledSelect } from '@shared/ui';
import { useBonusUsers, useManualBonusAdjust } from '@modules/bonuses';
import { formatPhoneNumber } from '@shared/utils';
import {
    manualBonusFormSchema,
    DEFAULT_MANUAL_COMMENT,
    type ManualBonusFormValues,
} from './manualBonusFormSchema';
import '@pages/BonusesPage/BonusesPage.css';

const DEFAULT_FORM_VALUES: ManualBonusFormValues = {
    userId: null,
    type: 'accrue',
    amountType: 'arbitrary',
    amount: undefined,
    orderAmount: undefined,
    expiresDays: undefined,
    description: DEFAULT_MANUAL_COMMENT,
};

export default function ManualBonusForm() {
    const manualAdjust = useManualBonusAdjust();

    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        formState: { errors },
    } = useForm<ManualBonusFormValues>({
        resolver: yupResolver(manualBonusFormSchema) as any,
        defaultValues: DEFAULT_FORM_VALUES,
    });

    const type = watch('type');
    const amountType = watch('amountType');
    const onlyShopClients = type === 'deduct';
    const { data: users = [], isLoading: usersLoading } = useBonusUsers(
        undefined,
        { onlyShopClients },
    );

    const prevTypeRef = useRef(type);
    useEffect(() => {
        if (prevTypeRef.current !== type) {
            prevTypeRef.current = type;
            setValue('userId', null);
        }
    }, [type, setValue]);

    const onSubmit = async (data: ManualBonusFormValues) => {
        const payload: Parameters<typeof manualAdjust.mutateAsync>[0] = {
            userId: Number(data.userId),
            type: data.type as 'accrue' | 'deduct',
            amount: 0,
            description: data.description || DEFAULT_MANUAL_COMMENT,
        };
        if (data.type === 'accrue') {
            payload.amountType = data.amountType as 'arbitrary' | 'from_order';
            if (data.amountType === 'from_order') {
                payload.orderAmount = Number(data.orderAmount);
            } else {
                payload.amount = Number(data.amount);
                if (data.expiresDays != null && data.expiresDays > 0) {
                    payload.expiresDays = data.expiresDays;
                }
            }
        } else {
            payload.amount = Number(data.amount);
        }
        await manualAdjust.mutateAsync(payload);
        reset();
    };

    const userOptions = useMemo(
        () =>
            users.map((u) => ({
                value: String(u.id),
                label: u.name ? `${formatPhoneNumber(u.phone)} (${u.name})` : formatPhoneNumber(u.phone),
                rawPhone: u.phone,
                searchName: (u.name ?? '').toLowerCase(),
            })),
        [users],
    );

    const filterUserOptions = useCallback(
        (input: { options: unknown[]; search: string }) => {
            const { options, search } = input;
            if (!search.trim()) return options;
            const searchLower = search.toLowerCase().trim();
            const searchDigits = search.replace(/\D/g, '');
            return options.filter((option: unknown) => {
                if (!option || typeof option !== 'object' || !('label' in option)) return true;
                const o = option as { label?: string; searchName?: string; rawPhone?: string };
                const labelMatch = (o.label ?? '').toString().toLowerCase().includes(searchLower);
                const nameMatch = (o.searchName ?? '').toString().includes(searchLower);
                const phoneDigits = o.rawPhone != null ? String(o.rawPhone).replace(/\D/g, '') : '';
                const phoneMatch =
                    searchDigits.length > 0 && phoneDigits.includes(searchDigits);
                return labelMatch || nameMatch || phoneMatch;
            });
        },
        [],
    );

    return (
        <div className="bonuses-manual-section">
            <h2 className="bonuses-form-title">Ручное начисление</h2>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="bonuses-manual-grid">
                    <div className="bonuses-form-field">
                        <ControlledSelect
                            label="Операция"
                            name="type"
                            control={control}
                            error={errors.type}
                            data={[
                                { value: 'accrue', label: 'Начислить' },
                                { value: 'deduct', label: 'Списать' },
                            ]}
                            description="Начисление или списание бонусов"
                        />
                    </div>
                    <div className="bonuses-form-field">
                        <ControlledSelect
                            label="Пользователь"
                            placeholder="Найдите по номеру"
                            name="userId"
                            control={control}
                            error={errors.userId}
                            data={userOptions}
                            searchable
                            clearable
                            disabled={usersLoading}
                            nothingFoundMessage="Пользователи не найдены"
                            description={
                                onlyShopClients
                                    ? 'Только клиенты с заказом в вашем магазине'
                                    : 'Все клиенты приложения'
                            }
                            filter={filterUserOptions as React.ComponentProps<typeof ControlledSelect>['filter']}
                        />
                    </div>
                    {type === 'accrue' && (
                        <div className="bonuses-form-field">
                            <ControlledSelect
                                label="Тип начисления"
                                name="amountType"
                                control={control}
                                error={errors.amountType}
                                data={[
                                    { value: 'arbitrary', label: 'Произвольная сумма' },
                                    { value: 'from_order', label: 'От суммы заказа' },
                                ]}
                                description="Фиксированная сумма или процент по правилу от заказа"
                            />
                        </div>
                    )}
                    {type === 'deduct' && (
                        <div className="bonuses-form-field">
                            <ControlledInput
                                id="manual-amount"
                                type="number"
                                label="Сколько списать"
                                name="amount"
                                control={control}
                                error={errors.amount}
                                placeholder="Бонусов"
                                min={1}
                                step={1}
                                description="Количество бонусов к списанию"
                            />
                        </div>
                    )}
                    {type === 'accrue' && amountType === 'from_order' && (
                        <div className="bonuses-form-field">
                            <ControlledInput
                                id="manual-orderAmount"
                                type="number"
                                label="Сумма заказа (₽)"
                                name="orderAmount"
                                control={control}
                                error={errors.orderAmount}
                                placeholder="Сумма заказа"
                                min={0}
                                step={100}
                                description="От этой суммы посчитается начисление по правилу"
                            />
                        </div>
                    )}
                    {type === 'accrue' && amountType === 'arbitrary' && (
                        <>
                            <div className="bonuses-form-field">
                                <ControlledInput
                                    id="manual-amount-accrue"
                                    type="number"
                                    label="Сколько начислить"
                                    name="amount"
                                    control={control}
                                    error={errors.amount}
                                    placeholder="Бонусов"
                                    min={1}
                                    step={1}
                                    description="Фиксированное количество бонусов"
                                />
                            </div>
                            <div className="bonuses-form-field">
                                <ControlledInput
                                    id="manual-expiresDays"
                                    type="number"
                                    label="Срок действия (дней)"
                                    name="expiresDays"
                                    control={control}
                                    error={errors.expiresDays}
                                    placeholder="Не ограничен"
                                    min={0}
                                    step={1}
                                    description="Пусто — бонусы не сгорают"
                                />
                            </div>
                        </>
                    )}
                    <div className="bonuses-form-field bonuses-manual-comment">
                        <ControlledInput
                            id="manual-description"
                            label="Комментарий"
                            name="description"
                            control={control}
                            error={errors.description}
                            placeholder={DEFAULT_MANUAL_COMMENT}
                            description="Будет видно в истории бонусов пользователя"
                        />
                    </div>
                </div>
                <div className="bonuses-form-actions">
                    <Button type="submit" variant="primary" disabled={manualAdjust.isPending} loading={manualAdjust.isPending}>
                        Выполнить
                    </Button>
                </div>
            </form>
        </div>
    );
}
